import { Schema as S } from 'effect';

import type { AllFiltersState } from '../state/all';
import { AllFilterStateMapsToApi } from './all';

/**
 * Flatten an {@link AllFiltersState} into the flat query-param record the backend
 * Filter classes consume. Shared by every product-listing endpoint that accepts
 * the same filter param names — `/shop/products` (taxon listing),
 * `/shop/search/products`, and `/shop/search/products/facets` — so the mapping
 * (and any new dimension, e.g. taxon) lives in one place instead of being copied
 * per request file.
 *
 * - `checkboxFilters` / `textFilters` / `rangeFilters` spread directly (their
 *   keys are already `attribute_<code>` / `price[gte]` etc.).
 * - `brands` collapse to a single comma-separated `brand` param.
 * - `warehouses` collapse to a `warehouseStock` param (`UUID:1,…`).
 * - `taxons` collapse to a single comma-separated `taxon` param (search rail only;
 *   empty for category listings, where the taxon comes from the route context).
 *
 * Empty dimensions contribute nothing, so passing `AllFiltersEmpty` yields `{}`.
 */
export const combineFiltersToApiQuery = (filters: AllFiltersState): Record<string, unknown> => {
  const apiData = S.decodeSync(AllFilterStateMapsToApi)(filters);

  const result: Record<string, unknown> = {
    ...apiData.checkboxFilters,
    ...apiData.textFilters,
    ...apiData.rangeFilters,
  };

  // Add brands if present (comma-separated string)
  if (apiData.brands.brand) {
    result.brand = apiData.brands.brand;
  }

  // Add warehouses if present (comma-separated string with UUID:1 format)
  if (apiData.warehouses.warehouseStock) {
    result.warehouseStock = apiData.warehouses.warehouseStock;
  }

  // Add taxons if present (comma-separated taxon codes)
  if (apiData.taxons.taxon) {
    result.taxon = apiData.taxons.taxon;
  }

  return result;
};
